import { resolveApiBaseUrl } from '../config/apiBaseUrl';

export const WAITING_LIST_TOKEN_KEY = 'visime_waiting_list_token';

export type StandingBucket = 'top_1' | 'top_10' | 'top_50' | 'in_list';

export type WaitingListStanding = {
  bucket: StandingBucket;
  label: string;
  referral_count: number;
};

export type WaitingListMembership = {
  message: string;
  already_joined: boolean;
  email: string;
  session_token: string;
  referral_code: string;
  referral_link: string;
  standing: WaitingListStanding;
};

export type WaitingListResult =
  | { ok: true; membership: WaitingListMembership }
  | { ok: false; message: string; status: number };

function persistToken(token: string | undefined) {
  if (typeof window === 'undefined') return;
  if (token) {
    window.localStorage.setItem(WAITING_LIST_TOKEN_KEY, token);
  }
}

export function readStoredWaitingListToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(WAITING_LIST_TOKEN_KEY);
}

export function clearStoredWaitingListToken() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(WAITING_LIST_TOKEN_KEY);
}

function asMembership(payload: Record<string, unknown>): WaitingListMembership | null {
  const sessionToken = typeof payload.session_token === 'string' ? payload.session_token : '';
  const referralCode = typeof payload.referral_code === 'string' ? payload.referral_code : '';
  const referralLink = typeof payload.referral_link === 'string' ? payload.referral_link : '';
  const email = typeof payload.email === 'string' ? payload.email : '';
  const standing = payload.standing;

  if (!sessionToken || !referralCode || !referralLink || !email || !standing || typeof standing !== 'object') {
    return null;
  }

  return {
    message:
      typeof payload.message === 'string' && payload.message
        ? payload.message
        : 'You are on the waiting list.',
    already_joined: Boolean(payload.already_joined),
    email,
    session_token: sessionToken,
    referral_code: referralCode,
    referral_link: referralLink,
    standing: standing as WaitingListStanding,
  };
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function joinWaitingList(
  email: string,
  referralCode?: string | null,
): Promise<WaitingListResult> {
  const body: { email: string; referral_code?: string } = { email: email.trim() };
  if (referralCode && referralCode.trim()) {
    body.referral_code = referralCode.trim();
  }

  const response = await fetch(`${resolveApiBaseUrl()}/api/waiting-list`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const payload = await readJson(response);
  const membership = asMembership(payload);

  // 201 created and 200 already-joined both carry the membership payload.
  if (membership && response.ok) {
    persistToken(membership.session_token);
    return { ok: true, membership };
  }

  return {
    ok: false,
    message:
      (typeof payload.message === 'string' && payload.message) ||
      'Could not join the waiting list. Please try again.',
    status: response.status,
  };
}

export async function fetchWaitingListStanding(
  token?: string | null,
): Promise<WaitingListResult> {
  const sessionToken = token ?? readStoredWaitingListToken();
  if (!sessionToken) {
    return { ok: false, message: 'No waiting-list session.', status: 401 };
  }

  const response = await fetch(`${resolveApiBaseUrl()}/api/waiting-list/me`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${sessionToken}`,
    },
  });

  if (response.status === 401) {
    clearStoredWaitingListToken();
    return { ok: false, message: 'Waiting-list session expired.', status: 401 };
  }

  const payload = await readJson(response);
  const membership = asMembership(payload);

  if (membership && response.ok) {
    persistToken(membership.session_token || sessionToken);
    return { ok: true, membership };
  }

  return {
    ok: false,
    message:
      (typeof payload.message === 'string' && payload.message) ||
      'Could not load waiting-list standing.',
    status: response.status,
  };
}
